/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import { Close } from "../icons";
import Input from "../common/Input";
import SelectInput from "../common/SelectInput";
import { useGetAllItemsQuery } from "../../store/api/itemApi";
import { useCreateCentralInventoryMutation, useGetMainCampusIdQuery } from "../../store/api";
import { toast } from "react-hot-toast";

export default function InitializeStockModal({ open, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    product: "",
    warehouse: "",
    quantity: "",
  });
  const [errors, setErrors] = useState([]);
  const [apiError, setApiError] = useState("");

  const { data: mainCampusData } = useGetMainCampusIdQuery();
  const mainCampusId = mainCampusData?.data?._id;

  useEffect(() => {
    if (open) {
      if (mainCampusId) {
        setFormData((prev) => ({ ...prev, warehouse: mainCampusId }));
      }
    } else {
      setFormData({ product: "", warehouse: "", quantity: "" });
      setErrors([]);
      setApiError("");
    }
  }, [open, mainCampusId]);

  // Fetch items for product dropdown
  const { data: itemsData, isLoading: itemsLoading } = useGetAllItemsQuery({
    page: 1,
    limit: 100,
  });


  // Map items to SelectInput format
  const items = itemsData?.data?.items || [];
  const itemOptions = items.map((item) => ({
    label: item.name,
    value: item._id,
  }));

  // Map main campus to SelectInput format
  const campusOptions = mainCampusData?.data ? [{
    label: mainCampusData.data.name,
    value: mainCampusData.data._id,
  }] : [];

  const [createCentralInventory, { isLoading: creating }] = useCreateCentralInventoryMutation();

  function handleSubmit() {
    // Validate all fields and collect errors
    const emptyFields = [];
    if (!formData.product) emptyFields.push("Product Catalog");
    if (!formData.warehouse) emptyFields.push("Central Warehouse");
    if (!formData.quantity) emptyFields.push("Opening Quantity");

    if (emptyFields.length > 0) {
      setErrors(emptyFields);
      setApiError("");
      return;
    }
    setErrors([]);

    // Prepare payload with correct field names
    const payload = {
      itemId: formData.product,
      campusId: formData.warehouse,
      stock: parseInt(formData.quantity),
    };

    createCentralInventory(payload)
      .unwrap()
      .then(() => {
        toast.success("Central inventory created successfully");
        onSubmit?.(formData);
        setFormData(prev => ({ ...prev, product: "", quantity: "" }));
        setApiError("");
        onClose?.();
      })
      .catch((err) => {
        const errorMessage = err?.data?.message || err.message || "Failed to create central inventory";
        toast.error(errorMessage);
        setApiError(errorMessage);
      });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={null}
      panelClassName="max-w-[416px] pt-8 pb-9 px-0"
      centered={false}
    >
      <div className="w-full">
        <div className="flex items-center justify-between mb-8 pl-7 pr-10">
          <p className="text-xl font-bold leading-6 text-black">
            Initialize Stock
          </p>
          <button onClick={onClose} aria-label="close">
            <Close />
          </button>
        </div>

        <div className="border-t border-[#E5E5E5] pt-3 pl-7 pr-10 space-y-2">
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-[5px] p-3 mb-3">
              <p className="text-red-600 text-sm font-medium">Please fill the following fields:</p>
              <ul className="text-red-600 text-sm mt-1 ml-2">
                {errors.map((error, idx) => (
                  <li key={idx}>• {error}</li>
                ))}
              </ul>
            </div>
          )}
          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-[5px] p-3 mb-3">
              <p className="text-red-600 text-sm font-medium">Error:</p>
              <p className="text-red-600 text-sm mt-1">{apiError}</p>
            </div>
          )}
          <SelectInput
            label="Product Catalog"
            options={itemOptions}
            value={formData.product}
            onChange={(val) => {
              setFormData({ ...formData, product: val });
              setApiError("");
            }}
            placeholder="Select an Item"
          />

          <SelectInput
            label="Central Warehouse"
            options={campusOptions}
            value={formData.warehouse}
            onChange={(val) => {
              setFormData({ ...formData, warehouse: val });
              setApiError("");
            }}
            placeholder="Select Central Campus"
            disabled={true}
          />

          <Input
            label="Opening Quantity"
            type="number"
            value={formData.quantity}
            onChange={(e) => {
              setFormData({ ...formData, quantity: e.target.value });
              setApiError("");
            }}
            placeholder="Enter Initial Stock level"
          />


          <button
            onClick={handleSubmit}
            disabled={creating}
            className={`w-full !mt-5 py-2.5 font-bold text-sm leading-6 rounded-[5px] transition ${creating
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[#6D4E8A] text-white hover:bg-[#5a3f75]"
              }`}
          >
            {creating ? "Creating..." : "Register Product"}
          </button>

        </div>
      </div>
    </Modal>
  );
}
